import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  filterTicketDocsForStatus,
  resolveTicketDocs,
  TICKET_DOCS_ANNOTATION,
  type TicketDocAttachment,
} from './ticketDocs';

type Props = {
  payload?: Record<string, unknown>;
  requestName?: string;
  templateEntityRef?: string;
  displayStatus: 'open' | 'in-progress' | 'closed';
  rejected?: boolean;
  cancelled?: boolean;
  /** Extra bottom margin when another section follows. */
  marginBottom?: number | string;
};

export function TicketDocsPanel({
  payload,
  requestName,
  templateEntityRef,
  displayStatus,
  rejected,
  cancelled,
  marginBottom = 0,
}: Props): JSX.Element | null {
  const catalogApi = useApi(catalogApiRef);
  const [annotationJson, setAnnotationJson] = useState<string | undefined>();

  useEffect(() => {
    let cancelledFetch = false;
    const ref = templateEntityRef?.trim();
    if (!ref) {
      setAnnotationJson(undefined);
      return undefined;
    }
    (async () => {
      try {
        const entity = await catalogApi.getEntityByRef(ref);
        if (cancelledFetch) return;
        const raw = entity?.metadata?.annotations?.[TICKET_DOCS_ANNOTATION];
        setAnnotationJson(typeof raw === 'string' ? raw : undefined);
      } catch {
        if (!cancelledFetch) setAnnotationJson(undefined);
      }
    })();
    return () => {
      cancelledFetch = true;
    };
  }, [catalogApi, templateEntityRef]);

  const docs: TicketDocAttachment[] = useMemo(() => {
    const resolved = resolveTicketDocs({
      payload,
      requestName,
      templateEntityRef,
      templateAnnotationJson: annotationJson,
    });
    return filterTicketDocsForStatus(resolved, displayStatus, {
      rejected,
      cancelled,
    });
  }, [
    payload,
    requestName,
    templateEntityRef,
    annotationJson,
    displayStatus,
    rejected,
    cancelled,
  ]);

  if (docs.length === 0) return null;

  // Group by topic so multiple links can share one heading.
  const byTopic = new Map<string, TicketDocAttachment[]>();
  for (const doc of docs) {
    const list = byTopic.get(doc.topic) ?? [];
    list.push(doc);
    byTopic.set(doc.topic, list);
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      id="ticket-docs"
      style={{
        marginBottom,
        padding: 16,
        borderRadius: 8,
        border: '1px solid rgba(0,0,0,0.12)',
        backgroundColor: 'rgba(0,0,0,0.02)',
        gap: 16,
      }}
    >
      {Array.from(byTopic.entries()).map(([topic, items]) => (
        <Box key={topic} display="flex" flexDirection="column" style={{ gap: 8 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
            {topic}
          </Typography>
          {items.map(item => (
            <Box key={`${item.topic}:${item.url}`} display="flex" flexDirection="column" style={{ gap: 8 }}>
              {item.description ? (
                <Typography variant="body2" color="textSecondary" component="div">
                  {item.description}
                </Typography>
              ) : null}
              <Button
                variant="outlined"
                color="primary"
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                {item.title}
              </Button>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}
